import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { theme, buttonClass } from '../ui/theme';
import { getDerivedVideoPath } from '../utils/musicStreamUrls';

/**
 * 下载选择对话框组件
 * @param {Object} music - 音乐对象
 * @param {boolean} open - 是否显示对话框
 * @param {Function} onClose - 关闭回调
 * @param {Function} onConfirm - 确认下载回调 (formats, includeLyrics) => void
 */
const DownloadDialog = ({ music, open, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [selectedFormats, setSelectedFormats] = useState(['mp3']);
  const [includeLyrics, setIncludeLyrics] = useState(false);
  const [availableFormats, setAvailableFormats] = useState([]);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const modalBaseZ = 100000;
  const overlayBaseClass = 'fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm';
  const panelBaseClass = [theme.surfaces.modalPanel, 'rounded-lg shadow-xl w-full mx-4'].join(' ');

  useEffect(() => {
    if (music && open) {
      // 解析metadata获取转码格式信息
      const metadata = music.metadata ? (typeof music.metadata === 'string' ? JSON.parse(music.metadata) : music.metadata) : null;
      const videoFile = getDerivedVideoPath(music);

      // 重置选择：有视频则默认选视频（视频内已包含音乐），否则默认 mp3
      setSelectedFormats(videoFile ? ['video'] : ['mp3']);
      setIncludeLyrics(false);

      // 检查可用的格式
      const formats = [];
      
      const convertedFormats = metadata?.convertedFormats || [];
      const originalWavPath = metadata?.originalWavPath;
      const videoExt = (() => {
        try {
          if (!videoFile) return '';
          const p = String(videoFile).split('?')[0];
          const ext = p.split('.').pop();
          return ext ? String(ext).toUpperCase() : '';
        } catch (_) {
          return '';
        }
      })();

      // 视频（若存在）
      if (videoFile) {
        formats.push({
          format: 'video',
          label: t('downloadDialog.formatVideo', { ext: videoExt || 'VIDEO' }),
          description: t('downloadDialog.formatVideoDesc', { ext: videoExt || 'VIDEO' })
        });
      }

      // 检查MP3（通常总是可用，因为它是主文件或转码格式之一）
      const hasMp3 = convertedFormats.some(f => f.format === 'mp3') || music.file_format === 'mp3';
      if (hasMp3) formats.push({ format: 'mp3', label: t('downloadDialog.formatMp3'), description: t('downloadDialog.formatMp3Desc') });

      // 检查Opus
      const hasOpus = convertedFormats.some(f => f.format === 'opus');
      if (hasOpus) formats.push({ format: 'opus', label: t('downloadDialog.formatOpus'), description: t('downloadDialog.formatOpusDesc') });

      // 检查FLAC
      const hasFlac = convertedFormats.some(f => f.format === 'flac');
      if (hasFlac) formats.push({ format: 'flac', label: t('downloadDialog.formatFlac'), description: t('downloadDialog.formatFlacDesc') });

      // 检查WAV（原始录音源）
      if (originalWavPath) formats.push({ format: 'wav', label: t('downloadDialog.formatWav'), description: t('downloadDialog.formatWavDesc') });

      setAvailableFormats(formats);
    }
  }, [music, open, t]);

  const handleFormatToggle = (format) => {
    setSelectedFormats(prev => {
      if (prev.includes(format)) {
        // 如果取消选择，至少保留一个格式
        if (prev.length > 1) {
          return prev.filter(f => f !== format);
        }
        return prev; // 至少保留一个
      } else {
        return [...prev, format];
      }
    });
  };

  const handleConfirm = () => {
    if (selectedFormats.length === 0) {
      alert(t('downloadDialog.selectAtLeastOne'));
      return;
    }
    // 显示提示框而不是直接下载
    setShowNoticeModal(true);
  };

  const handleNoticeClose = () => {
    setShowNoticeModal(false);
    // 由于下载服务尚未上线，关闭提示框后也关闭下载对话框，不执行下载
    onClose();
  };

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className={overlayBaseClass} style={{ zIndex: modalBaseZ }} onClick={onClose}>
        <div className={[panelBaseClass, 'max-w-md'].join(' ')} onClick={(e) => e.stopPropagation()}>
          <div className="p-4">
            <h2 className="text-lg font-semibold text-white mb-3">{t('downloadDialog.title')}</h2>
            
            {/* 格式选择 */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-white/80 mb-2">{t('downloadDialog.formatLabel')}</label>
              <div className="space-y-1.5">
                {availableFormats.map(format => (
                  <label
                    key={format.format}
                    className={`flex items-center p-2 rounded cursor-pointer transition-colors border ${
                      selectedFormats.includes(format.format)
                        ? 'border-blue-400/50 bg-blue-500/10'
                        : 'border-white/10 hover:border-blue-400/25 bg-white/[0.03]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFormats.includes(format.format)}
                      onChange={() => handleFormatToggle(format.format)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div className="ml-2 flex-1">
                      <div className="text-sm font-medium text-white">{format.label}</div>
                      <div className="text-xs text-white/55">{format.description}</div>
                    </div>
                  </label>
                ))}
              </div>
              {availableFormats.length === 0 && (
                <p className="text-xs text-white/55 mt-2">{t('downloadDialog.noFormatsAvailable')}</p>
              )}
            </div>

            {/* 歌词下载选项 */}
            {music?.lyrics_type && (
              <div className="mb-4">
                <label className="flex items-center p-2 border border-white/10 bg-white/[0.03] rounded cursor-pointer hover:border-blue-400/25 transition-colors">
                  <input
                    type="checkbox"
                    checked={includeLyrics}
                    onChange={(e) => setIncludeLyrics(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div className="ml-2">
                    <div className="text-sm font-medium text-white">{t('downloadDialog.includeLyrics')}</div>
                    <div className="text-xs text-white/55">{t('downloadDialog.lyricsFormat', { format: String(music.lyrics_type || '').toUpperCase() })}</div>
                  </div>
                </label>
              </div>
            )}

            {/* 按钮 */}
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className={buttonClass({ variant: 'ghost', size: 'sm' })}
              >
                {t('downloadDialog.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                className={buttonClass({ variant: 'outline', size: 'sm' })}
              >
                {t('downloadDialog.startDownload', { count: selectedFormats.length, lyrics: includeLyrics ? t('downloadDialog.withLyrics') : '' })}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 下载服务提示框 */}
      {showNoticeModal && (
        <div className={overlayBaseClass} style={{ zIndex: modalBaseZ + 10 }} onClick={handleNoticeClose}>
          <div 
            className={[panelBaseClass, 'max-w-lg overflow-hidden transform transition-all'].join(' ')}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 顶部装饰条 */}
            <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
            
            <div className="p-6">
              {/* 图标和标题 */}
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-2xl font-semibold text-white text-center mb-6">
                下载服务即将上线
              </h3>
              
              {/* 内容区域 */}
              <div className="space-y-4 text-white/75">
                <div className="bg-blue-500/10 border-l-4 border-blue-400/50 p-4 rounded-r-lg">
                  <p className="text-base font-medium text-white mb-1">
                    📅 预计上线时间
                  </p>
                  <p className="text-sm text-white/75">
                    预计 <span className="font-semibold text-blue-200">1月15日</span> 提供下载服务，敬请期待。
                  </p>
                </div>
                
                <div className="bg-purple-500/10 border-l-4 border-purple-400/50 p-4 rounded-r-lg">
                  <p className="text-base font-medium text-white mb-1">
                    👑 会员制服务
                  </p>
                  <p className="text-sm text-white/75">
                    本站下载实行会员制。
                  </p>
                </div>
                
                <div className="bg-emerald-500/10 border-l-4 border-emerald-400/50 p-4 rounded-r-lg">
                  <p className="text-base font-medium text-white mb-2">
                    ✨ 会员权益
                  </p>
                  <p className="text-sm text-white/75 leading-relaxed">
                    对于 <span className="font-semibold">月度会员</span>、<span className="font-semibold">年度会员</span> 和 <span className="font-semibold">终身会员</span>，我们授予永久、全球、非独占且可再许可的许可，以使用、编辑和商业利用下载的曲目。
                  </p>
                </div>
              </div>
              
              {/* 按钮 */}
              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleNoticeClose}
                  className={buttonClass({ variant: 'outline', size: 'md' })}
                >
                  我知道了
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default DownloadDialog;

